import { api } from '../../../lib/api.js';

const USER_PROFILE_FIELDS = [
  'gender',
  'user_type',
  'birth_date',
  'user_education',
  'is_english_medium',
  'aspect_to_develop',
  'graduate_year',
  'primary_purpose',
  'user_job',
  'job_type',
  'preparing_exam',
  'skills',
  'isParent',
  'receiveWhatsappNotification',
  'whatsappNumber',
  'childrenAge',
  'age',
  'years_of_schooling',
  'most_common_medium_of_instruction',
  'home_language',
  'num_langs_speak_or_understand',
  'num_langs_read_and_write',
  'occupation',
  'user_purpose',
  'fcm_token',
  'app_instance_id',
  'isMobVerified',
  'isSubscribe',
];

const USER_PROFILE_DEFAULTS = {
  age: '',
  gender: '',
  whatsappNumber: '',
  receiveWhatsappNotification: '0',
  isSubscribe: '0',
};

const USER_PROFILE_BOOLEAN_FLAGS = new Set([
  'isParent',
  'receiveWhatsappNotification',
  'isMobVerified',
  'isSubscribe',
]);

function normalizeProfileFlag(value) {
  return value === true || value === 1 || value === '1' ? '1' : '0';
}

export function normalizeProfile(profile = {}) {
  if (!profile || typeof profile !== 'object') return profile;

  const nestedProfile = profile.user_profile && typeof profile.user_profile === 'object'
    ? profile.user_profile
    : {};
  const normalized = {
    ...profile,
    user_profile: nestedProfile,
  };

  USER_PROFILE_FIELDS.forEach((field) => {
    const fallback = Object.prototype.hasOwnProperty.call(USER_PROFILE_DEFAULTS, field)
      ? USER_PROFILE_DEFAULTS[field]
      : '';
    const value = nestedProfile[field] ?? profile[field] ?? fallback;
    normalized[field] = USER_PROFILE_BOOLEAN_FLAGS.has(field)
      ? normalizeProfileFlag(value)
      : value;
  });

  if (!String(normalized.whatsappNumber ?? '').trim()) {
    normalized.receiveWhatsappNotification = '0';
  }

  return normalized;
}

export function serializeProfileBody(profile = {}) {
  if (!profile || typeof profile !== 'object') return profile;

  const nestedProfile = profile.user_profile && typeof profile.user_profile === 'object'
    ? { ...profile.user_profile }
    : {};

  USER_PROFILE_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(profile, field)) {
      nestedProfile[field] = profile[field];
    }
  });

  return {
    ...profile,
    user_profile: nestedProfile,
  };
}

export async function fetchProfile() {
  const res = await api.get('v3/user_profile');
  return {
    ...res.data,
    data: normalizeProfile(res.data?.data),
  };
}

export async function updateProfile(body) {
  // v3/user_profile expects the same flat profile shape used by production.
  const res = await api.post('v3/user_profile', serializeUserProfilePayload(body), {
    suppressUnauthorizedRedirect: true,
  });
  return res.data;
}

export function serializePhoneChangeOtpBody(profile = {}) {
  return {
    age: String(profile.age ?? ''),
    email: profile.email ?? '',
    full_name: profile.full_name ?? '',
    gender: profile.gender ?? '',
    isParent: String(profile.isParent ?? '0'),
    isSubscribe: String(profile.isSubscribe ?? '0'),
    phone: profile.phone ?? '',
    primary_purpose: profile.primary_purpose || 'Others',
    receiveWhatsappNotification: String(profile.receiveWhatsappNotification ?? '1'),
    user_type: profile.user_type || 'Others',
    whatsappNumber: profile.whatsappNumber || profile.phone || '',
  };
}

export function serializeUserProfilePayload(profile = {}) {
  const whatsappNumber = String(profile.whatsappNumber ?? '').trim();
  return {
    age: String(profile.age ?? ''),
    email: profile.email ?? '',
    full_name: profile.full_name ?? '',
    gender: profile.gender ?? '',
    isParent: String(profile.isParent ?? '0'),
    isSubscribe: String(profile.isSubscribe ?? '0'),
    phone: profile.phone ?? '',
    primary_purpose: profile.primary_purpose || 'Others',
    receiveWhatsappNotification: whatsappNumber ? String(profile.receiveWhatsappNotification ?? '0') : '0',
    user_type: profile.user_type || 'Others',
    whatsappNumber,
  };
}

export async function requestPhoneChangeOtp(body) {
  const res = await api.post('v3/user_profile', serializePhoneChangeOtpBody(body), {
    suppressUnauthorizedRedirect: true,
  });
  return res.data;
}

export async function completeOnboarding(body) {
  const res = await api.post('onboarding', body);
  return res.data;
}

export async function requestPinReset() {
  // GET resetpin triggers a server-side new PIN email/SMS. No body.
  const res = await api.get('resetpin');
  return res.data;
}

export const requestPasswordReset = requestPinReset;

export async function fetchNotifyPrefs() {
  const res = await api.get('checknotify');
  return res.data;
}

export async function updateNotifyPrefs(notifyIsOn) {
  // Legacy uses POST (not PUT) — apps/legacy/src/redux/settings/settings.sagas.js:215
  const res = await api.post('checknotify', { notify_is_on: notifyIsOn });
  return res.data;
}

export async function fetchSpeechPrefs() {
  const res = await api.get('checkspeech');
  return res.data;
}

export async function updateSpeechPrefs(speechIsOn) {
  // Legacy settings toggles speech through checkspeech with this exact key.
  const res = await api.post('checkspeech', { speech_is_on: speechIsOn });
  return res.data;
}

export async function fetchOrders() {
  const res = await api.get('orders');
  return res.data;
}

export async function fetchCertificates() {
  const res = await api.get('listcertificates');
  return res.data;
}

export async function fetchLicenses() {
  const res = await api.get('licenses');
  return res.data;
}

// POST /v1/deleteaccount deletes the authenticated account. The endpoint
// identifies the account from the access-token header, so it takes no body.
export async function deleteAccount() {
  const res = await api.post('deleteaccount');
  return res.data;
}
